import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import * as topojson from "topojson-client";
import { ATSC3 } from "./ChannelData.js";
import { useTheme } from "@mui/material/styles";

export default function ATSCCountyMap({ dmaData, dmaMapData, countyMapData, setMarketData, ATSCType }) {
    const svgRef = useRef();
    const tooltipRef = useRef(null);

    const theme = useTheme();

    const colors = useMemo(() => ({
        stateFill: theme.palette.Map.landscape.fill,
        stateStroke: theme.palette.Map.landscape.stroke,
        countyFill: theme.palette.Map.item.fill,
        countyStroke: theme.palette.Map.item.stroke,
    }), [theme]);

    const [tooltipData, setTooltipData] = useState({
        open: false,
        title: "",
        tvPerc: "",
        cablePerc: "",
        type: ""
    });

    let ATSC3Markets = useMemo(() => (ATSCType === "ATSC3.0" ? ATSC3() : []), [ATSCType]);

    // Use useEffect to draw the map when the data is loaded
    useEffect(() => {

        // Check if the map json is loaded
        if (!dmaMapData || !countyMapData) return;

        // Create the SVG element
        // Clear the SVG before drawing
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const width = 975;
        const height = 610;


        // Set the SVG attributes
        svg
            .attr("viewBox", [0, 0, width, height])
            .attr("width", width)
            .attr("height", height)
            .style("max-width", "100%")
            .style("height", "auto")
            .style("cursor", "pointer")
            .on("click", function (event) {
                if (event.target.tagName === "svg") resetZoom();
            });

        // Create a group element to hold the map
        const g = svg.append("g");
        
        // Define the projection and path generator
        const projection = d3.geoAlbersUsa().scale(1300).translate([487.5, 305]);
        const path = d3.geoPath(projection);
        const zoom = d3.zoom()
            .scaleExtent([1, 8])
            .translateExtent([[-50, -50], [width + 50, height + 50]])
            .on("zoom", (event) => {
                g.attr("transform", event.transform);
                g.attr("stroke-width", 1 / event.transform.k);
                g.select(".counties").style("opacity", event.transform.k > 3 ? 1 : 0)
                    .attr("visibility", event.transform.k > 3 ? "visible" : "hidden");
            });

        svg.call(zoom);

        const DMA = topojson.feature(dmaMapData, dmaMapData.objects.nielsen_dma).features;
        const counties = topojson.feature(countyMapData, countyMapData.objects.counties).features;

        // Set the market data for the ToolTip
        const showTooltip = (event, d, type) => {
            const tooltipEl = tooltipRef.current;
            if (tooltipEl) {
                tooltipEl.style.top = `${event.pageY + 15}px`;
                tooltipEl.style.left = `${event.pageX + 15}px`;
            }

            if (type === "DMA") {
                setTooltipData({
                    open: true,
                    title: d.properties.dma1,
                    tvPerc: d.properties.tvperc,
                    cablePerc: d.properties.cableperc,
                    type: "DMA"
                });
            } else {
                setTooltipData({
                    open: true,
                    title: d.properties.NAME,
                    tvPerc: "",
                    cablePerc: d.properties.COUNTYFP,
                    type: "FIPS"
                });
            }
        };

        // Draw the DMA map
        g.append("g")
            .attr("class", "dma")
            .selectAll("path")
            .data(DMA)
            .join("path")
            .attr("fill", d => ATSC3Markets.includes(d.properties.dma1) ? colors.countyFill : colors.stateFill)
            .attr("stroke", colors.stateStroke)
            .attr("cursor", "pointer")
            .attr("d", path)
            .on("mouseover", (e, d) => showTooltip(e, d, "DMA"))
            .on("mousemove", (e, d) => showTooltip(e, d, "DMA"))
            .on("mouseout", () => setTooltipData(prev => ({ ...prev, open: false })))
            .on("click", (_, d) => zoomToFeature(d));

        // Draw the counties map
        g.append("g")
            .attr("class", "counties")
            .style("opacity", 0)
            .attr("visibility", "hidden")
            .selectAll("path")
            .data(counties)
            .join("path")
            .attr("fill", "transparent")
            .attr("stroke", colors.countyStroke)
            .attr("cursor", "pointer")
            .attr("d", path)
            .on("mouseover", (e, d) => showTooltip(e, d, "FIPS"))
            .on("mousemove", (e, d) => showTooltip(e, d, "FIPS"))
            .on("mouseout", () => setTooltipData(prev => ({ ...prev, open: false })))
            .on("click", (_, d) => zoomToFeature(d));

        let focusedFeature = null;
        function zoomToFeature(feature) {
            if (focusedFeature === feature) {
                resetZoom();
                focusedFeature = null;
                return;
            }
            focusedFeature = feature;
            const [[x0, y0], [x1, y1]] = path.bounds(feature);
            const dx = x1 - x0;
            const dy = y1 - y0;
            const x = (x0 + x1) / 2;
            const y = (y0 + y1) / 2;
            const scale = Math.max(1, Math.min(8, 0.9 / Math.max(dx / width, dy / height)));
            const translate = [width / 2 - scale * x, height / 2 - scale * y];

            svg.transition()
                .duration(750)
                .call(zoom.transform, d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale));
        }

        function resetZoom() {
            svg.transition()
                .duration(750)
                .call(zoom.transform, d3.zoomIdentity);
        }

    }, [dmaMapData, countyMapData, ATSC3Markets, colors]);

    return (
        <>
            <svg ref={svgRef}></svg>
            <div
                ref={tooltipRef}
                style={{
                    position: "absolute",
                    display: tooltipData.open ? "block" : "none",
                    pointerEvents: "none",
                    background: "white",
                    border: "1px solid #ccc",
                    padding: "5px",
                    color: "black",
                    maxWidth: "300px",
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    zIndex: 1000
                }}
            >
                <strong>{tooltipData.title}</strong>
                <div style={{ marginTop: "5px" }}>
                    {tooltipData.type === "DMA" && (
                        <>
                            <strong>TV Households:</strong> {tooltipData.tvPerc}%<br />
                            <strong>Cable Households:</strong> {tooltipData.cablePerc}%
                        </>
                    )}
                    {tooltipData.type === "FIPS" && (
                        <>
                            <strong>County FIP:</strong> {tooltipData.cablePerc}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
